import { showSuccess } from '@calendly/alert'
import { useOnValueChange } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import { Badge } from '@calendly/ui/components/badge'
import { BlockIcon } from '@calendly/ui/components/icons'
import { MinimalButton } from '@calendly/ui/components/minimal-button'
import { PageHeader } from '@calendly/ui/components/page-header'
import { BodyText } from '@calendly/ui/components/typography'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { useState } from 'react'
import { useDispatch } from 'react-redux'

import { useEnforceUpgrade } from 'components/billing/useEnforceUpgrade'
import { LoadingWrapper } from 'components/core/loading_state'
import {
  useUpdateUserMeetingIntelligenceDataMutation,
  useGetUserCalendarsQuery,
  useGetMeetingIntelligenceUserSettingsQuery,
  useDeleteMeetingIntelligenceUserMeetingDataMutation,
} from 'features/meeting_intelligence/api'
import { IntegrationsProvider } from 'features/meeting_intelligence/components/recording_rules/integrations_provider'
import { NotetakerSettings } from 'features/meeting_intelligence/components/recording_rules/notetaker_settings'
import { useEnableNotetakerWithLoader, useNotetakerFreeMeetingsQuota } from 'features/meeting_intelligence/hooks'
import { MeetingIntelligenceDeleteModal, MeetingIntelligenceDisableModal } from 'features/meeting_intelligence/modals'
import { getNotetakerAccessStartedAction, userSettingsTrackingV3 } from 'features/meeting_intelligence/tracking'
import { useIsSettingsModalLayout } from 'features/settings_modal/layout_context'
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants'
import { useTrackOnce, useTrack } from 'hooks/analytics_events/useTrack'
import { useNotetakerFiveFreeRecapsExperiment } from 'hooks/useNotetakerFiveFreeRecapsExperiment'
import { useNotetakerFreeExperiment } from 'hooks/useNotetakerFreeExperiment'

import { EmptyState } from '../components/empty_state'
import { MeetingIntelligenceProvider } from '../provider'
import { SettingsModalWrapper, SettingsWrapper, ModifiedPageContainer } from './styles'
import { SubmitCancelSettings } from './submit_cancel_settings'
import { NotetakerSettingsProvider } from '../notetaker_settings_provider'

export const handleEmptyStateClick = ({
  adminDisabledNotetaker,
  track,
  enforceUpgrade,
  enableNotetaker,
  hasRemainingFreeMeetings,
  isInNotetakerFreeExperiment = false,
}) => {
  // Prevent modal from showing if admin has disabled notetaker for the organization
  if (adminDisabledNotetaker) {
    return
  }

  track({
    name: TRACKING_EVENT_NAMES.CTA_CLICKED,
    entities: ['attribution', 'organization', 'user'],
    properties: {
      cta_key: 'ai_notetaker_settings',
      cta_text: 'get_started_ai_notetaker',
      cta_destination: 'ai_notetaker_setup_flow',
      action: getNotetakerAccessStartedAction(isInNotetakerFreeExperiment)
    }
  })

  if (hasRemainingFreeMeetings) {
    enableNotetaker()
    return
  }

  enforceUpgrade(enableNotetaker)
}

export const MeetingIntelligenceUserSettings = () => {
  const inSettingsModal = useIsSettingsModalLayout()
  const dispatch = useDispatch()
  const track = useTrack()
  const [showDisableModal, setShowDisableModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [trackingUpdateValues, _setTrackingUpdateValues] = useState(null)
  // 'meeting_intelligence' here is the billing feature key, NOT the legacy meeting_intelligence feature flag
  const enforceUpgrade = useEnforceUpgrade('meeting_intelligence')
  const enableNotetaker = useEnableNotetakerWithLoader()

  const [update, { isSuccess: updateSuccess }] = useUpdateUserMeetingIntelligenceDataMutation()
  const [deleteData] = useDeleteMeetingIntelligenceUserMeetingDataMutation()

  const {
    isLoading: isMeetingIntelligenceLoading,
    data: meetingIntelligenceData
  } = useGetMeetingIntelligenceUserSettingsQuery({}, { refetchOnMountOrArgChange: true })

  const {
    isLoading: isCalendarDataLoading,
    data: calendarData
  } = useGetUserCalendarsQuery({}, { refetchOnMountOrArgChange: true })

  const isLoading = isMeetingIntelligenceLoading
    || isCalendarDataLoading
  const { isInExperiment: isInNotetakerFiveFreeRecapsExperiment } = useNotetakerFiveFreeRecapsExperiment(
    !isLoading && !meetingIntelligenceData?.miEnabled
  )
  const { isInExperiment: isInNotetakerFreeExperiment } = useNotetakerFreeExperiment()
  const isInMeteredNotetakerExperiment = isInNotetakerFreeExperiment || isInNotetakerFiveFreeRecapsExperiment
  const adminDisabledNotetaker = (meetingIntelligenceData?.adminDisabledNotetaker && !isLoading) || false
  const { hasRemainingFreeMeetings } = useNotetakerFreeMeetingsQuota({
    skip: !isInMeteredNotetakerExperiment
  })

  const handleDeleteMeetingData = () => {
    deleteData()
    setShowDeleteModal(false)
  }

  const handleDisableMeetingRecaps = () => {
    update({ miEnabled: false })
  }

  const handleTrackingUpdate = () => {
    const trackingConfig = userSettingsTrackingV3.valuesSaved
    const trackingValues = {
      name: trackingConfig.name,
      properties: {
        ...trackingConfig.props,
        edit_type: [JSON.stringify(trackingUpdateValues)]
      }
    }
    track(trackingValues)
  }

  const handleTrackModal = (key, additionalProps = {}) => {
    const trackingConfig = userSettingsTrackingV3[key]
    const trackingValues = {
      name: trackingConfig.name,
      properties: {
        ...trackingConfig.props,
        ...additionalProps
      }
    }
    track(trackingValues)
  }

  const onEmptyStateClick = () => {
    handleEmptyStateClick({
      adminDisabledNotetaker: meetingIntelligenceData?.adminDisabledNotetaker,
      track,
      enforceUpgrade,
      enableNotetaker,
      hasRemainingFreeMeetings,
      isInNotetakerFreeExperiment,
    })
  }

  useOnValueChange(updateSuccess, () => {
    if (updateSuccess) {
      dispatch(showSuccess(I18n.t('publisher.account_settings.meeting_intelligence.success')))
      // TODO: null guard suppresses null events from firing on save.
      // trackingUpdateValues is always null here because _setTrackingUpdateValues is
      // never called. We need to add a disableModalConfirm key to
      // userSettingsTrackingV3 and fire it directly in handleDisableMeetingRecaps,
      // then remove this useOnValueChange callback entirely (save-path tracking now
      // lives in SubmitCancelSettings).
      if (trackingUpdateValues !== null) {
        handleTrackingUpdate()
      }
    }
  })

  useTrackOnce((!meetingIntelligenceData?.miEnabled && !isLoading), {
    name: TRACKING_EVENT_NAMES.CTA_SHOWN,
    entities: ['attribution', 'organization', 'user'],
    properties: {
      cta_key: 'ai_notetaker_settings',
      cta_text: 'get_started_ai_notetaker',
      cta_destination: 'ai_notetaker_setup_flow',
    }
  })

  const meetingIntelligenceContext = {
    isLoading,
    meetingIntelligenceData,
    calendarData,
    update,
  }

  const isPhoneWidth = useDeviceType('isPhone')
  const disableText = isPhoneWidth
    ? I18n.t('publisher.personal_settings.meeting_recaps.disable_meeting_recaps_mobile')
    : I18n.t('publisher.personal_settings.meeting_recaps.disable_meeting_recaps')
  const Wrapper = inSettingsModal
    ? SettingsModalWrapper
    : SettingsWrapper

  return (
    <MeetingIntelligenceProvider {...meetingIntelligenceContext}>
      <NotetakerSettingsProvider>
        <IntegrationsProvider>
          <LoadingWrapper isLoading={isLoading}>
            {!meetingIntelligenceData?.miEnabled ? (
              <EmptyState
                setShowDeleteModal={setShowDeleteModal}
                handleTrackModal={handleTrackModal}
                wasPreviouslyEnabled={meetingIntelligenceData?.userHasEnabledMiBefore}
                handleEmptyStateClick={onEmptyStateClick}
                adminDisabledNotetaker={adminDisabledNotetaker}
              />
            ) : (
              <ModifiedPageContainer>
                {!inSettingsModal && (
                  <PageHeader
                    actions={[
                      <MinimalButton
                        decoration="danger"
                        onClick={() => {
                          handleTrackModal('disableModalOpen')
                          setShowDisableModal(true)
                        }}
                      >
                        <BlockIcon />
                        {disableText}
                      </MinimalButton>
                    ]}
                    badge={
                      adminDisabledNotetaker ? (
                        <Badge type="error">{I18n.t('publisher.personal_settings.meeting_recaps.disabled')}</Badge>
                      ) : (
                        <Badge type="success">{I18n.t('publisher.personal_settings.meeting_recaps.enabled')}</Badge>
                      )
                    }
                    eyebrow={[
                      <BodyText weight="bold" color="muted">
                        {I18n.t('publisher.personal_settings.navigation.account_details')}
                      </BodyText>
                    ]}
                    title={I18n.t('publisher.personal_settings.meeting_recaps.meeting_recaps')}
                  />
                )}
                <Wrapper>
                  <NotetakerSettings />
                </Wrapper>
                <SubmitCancelSettings
                  handleTrackModal={handleTrackModal}
                  setShowDeleteModal={setShowDeleteModal}
                  adminDisabledNotetaker={adminDisabledNotetaker}
                />
                {showDisableModal && (
                  <MeetingIntelligenceDisableModal
                    handleTrackModal={handleTrackModal}
                    onClose={() => setShowDisableModal(false)}
                    onConfirm={() => {
                      handleDisableMeetingRecaps()
                      setShowDisableModal(false)
                    }}
                  />
                )}
              </ModifiedPageContainer>
            )}
            {showDeleteModal && (
              <MeetingIntelligenceDeleteModal
                handleTrackModal={handleTrackModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={() => {
                  handleDeleteMeetingData()
                  handleTrackModal('deleteModalConfirm')
                }}
              />
            )}
          </LoadingWrapper>
        </IntegrationsProvider>
      </NotetakerSettingsProvider>
    </MeetingIntelligenceProvider>
  )
}
